import {Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {IProduct} from '../../../../core/models/product.model';
import {DomSanitizer, SafeUrl} from '@angular/platform-browser';
import PptxGenJS from 'pptxgenjs';
import _ from 'lodash';
import jsPDF, {GState, jsPDFOptions} from 'jspdf';
import {IDisplayMetric} from '../../../../core/models/display-metric.model';
import {NotificationService} from '../../../../core/core.module';
import {FormatDisplayNamePipe} from '../../../../core/data/format-display-name.pipe';
import {ShortNumberPipe} from '../../../../shared/pipes/short-number.pipe';
import {
    composeHeaderBar,
    composeOverlay,
    computePageGrid,
    fitContain,
    HEADER_FONT_RATIO,
    IBox,
    IPageGrid,
    ITextBlock,
    ITextMeasurer,
    LINE_HEIGHT_FACTOR,
    mapWithConcurrency,
    parseLayout,
    SUB_HEADER_FONT_RATIO
} from './gallery-export-layout';
import {IExportImage, loadExportImage} from './gallery-export-images';

type IGalleryItem = IProduct & { imageId: string; image_url: SafeUrl };
type ImageLoadStatus = 'pending' | 'loading' | 'done' | 'failed';

interface IComposedPage {
    header?: ITextBlock;
    subHeader?: ITextBlock;
    tiles: Array<{ box: IBox; item?: IGalleryItem; overlay?: ITextBlock }>;
}

// Colours mirror gallery-export.component.scss so the file looks like the preview.
const HEADER_COLOUR: string = '#000000';
const SUB_HEADER_COLOUR: string = '#A5A5A5';
const TILE_COLOUR: string = '#F5F5F5';
const MISSING_IMAGE_COLOUR: string = '#D3D3D3';
const OVERLAY_COLOUR: string = '#000000';
const OVERLAY_OPACITY: number = 0.5;
const TEXT_COLOUR: string = '#FFFFFF';
const EXPORT_IMAGE_DPI: number = 300;
// Up to 60 full-size originals: unbounded parallel fetches stall the browser and the CDN.
const IMAGE_LOAD_CONCURRENCY: number = 6;

// Resolves after the browser has painted, so progress shown just before synchronous work is
// actually visible.
function nextPaint(): Promise<void> {
    return new Promise((resolve: () => void) => requestAnimationFrame(() => setTimeout(resolve)));
}

function hex(colour: string): string {
    return colour.replace('#', '');
}

function createJsPdfMeasurer(pdf: jsPDF): ITextMeasurer {
    const useFont: (fontPt: number) => void = (fontPt: number): void => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(fontPt);
    };

    return {
        widthOf: (text: string, fontPt: number): number => {
            useFont(fontPt);

            return pdf.getTextWidth(text);
        },
        splitToWidth: (text: string, fontPt: number, maxWidthIn: number): Array<string> => {
            useFont(fontPt);

            return pdf.splitTextToSize(text, maxWidthIn);
        }
    };
}

@Component({
    selector: 'lynkd-pattern-gallery-export',
    templateUrl: './gallery-export.component.html',
    styleUrls: ['./gallery-export.component.scss']
})
export class GalleryExportComponent implements OnInit {
    public galleryItems: Array<IGalleryItem>;
    public galleryItemsPages: Array<Array<IGalleryItem>>;
    public exportCap: number = 60;
    public isExporting: boolean = false;
    public exportStatus: string = '';
    public previewPageIndex: number = 0;
    public layoutConfigs: Array<{
        layout: string;
        pageSize: number;
    }> = [
        {
            pageSize: 18,
            layout: '6x3'
        },
        {
            pageSize: 6,
            layout: '3x2'
        },
        {
            pageSize: 8,
            layout: '4x2'
        }
    ];
    public selectedConfig: {
        layout: string;
        pageSize: number;
    };
    public pageSizes: Array<{ name: string; width?: number; height?: number }> = [
        {
            name: 'A3',
            width: 16.5,
            height: 11.7
        },
        {
            name: 'A4',
            width: 11.7,
            height: 8.3
        }
    ];

    public fileHeader: string;
    public fileSubHeader: string;
    public exportType: string;
    public filename: string;
    public availableMetrics: Array<string>;
    public selectedPageSize: { name: string; width?: number; height?: number };

    private readonly _displayName: FormatDisplayNamePipe = new FormatDisplayNamePipe();
    private readonly _shortNumber: ShortNumberPipe = new ShortNumberPipe();
    private readonly _imageStatus: Map<string, ImageLoadStatus> = new Map();

    public constructor(
        @Inject(MAT_DIALOG_DATA) public readonly data: { products: Array<IProduct>; filterData: Array<IDisplayMetric>; metricAlignment: boolean },
        private readonly _dialogRef: MatDialogRef<GalleryExportComponent>,
        private readonly _notificationService: NotificationService,
        private readonly _sanitizer: DomSanitizer
    ) {
    }

    public ngOnInit(): void {
        this.setInitialLayoutConfig();
        this.initializeGallery();
    }

    public setGalleryLayout(): void {
        this.initializeGallery();
    }

    public getLayout(): string {
        if (this.selectedConfig.layout === '6x3') {
            return `gallery-item-6x3`;
        } else if (this.selectedConfig.layout === '3x2') {
            return `gallery-item-3x2`;
        } else if (this.selectedConfig.layout === '4x2') {
            return `gallery-item-4x2`;
        } else {
            return `gallery-item-6x3`;
        }
    }

    public getMetricStyle(): Record<string, string> {
        if (this.data.metricAlignment === false) {
            return { bottom: '0', top: 'unset' };
        } else {
            return { top: '0', bottom: 'unset' };
        }
    }

    public getTileStatus(item: IGalleryItem): ImageLoadStatus | undefined {
        return this.isExporting && item ? this._imageStatus.get(item.imageId) : undefined;
    }

    // The tick/warning badge goes in the corner the metrics overlay is not using.
    public getBadgeStyle(): Record<string, string> {
        return this.data.metricAlignment === false ? { top: '4px' } : { bottom: '4px' };
    }

    public async exportAsPdf(): Promise<void> {
        this.isExporting = true;
        // Snapshot: the preview is paged through during the export, and a layout change made
        // meanwhile must not mix two different pagings into one file.
        const pages: Array<Array<IGalleryItem>> = this.galleryItemsPages;

        try {
            const format: string = this.selectedPageSize.name.toLowerCase();
            const options: jsPDFOptions = {
                orientation: 'l',
                unit: 'in',
                format
            };
            const pdf: jsPDF = new jsPDF(options);
            const measurer: ITextMeasurer = createJsPdfMeasurer(pdf);
            const grid: IPageGrid = this.computeGrid(pdf.internal.pageSize.getWidth(), pdf.internal.pageSize.getHeight());
            const images: Map<string, IExportImage | null> = await this.loadImages(grid, pages);

            for (const [index, page] of pages.entries()) {
                await this.showBuildProgress(pages, index);
                try {
                    if (index !== 0) {
                        pdf.addPage(format, 'l');
                    }

                    this.drawPdfPage(pdf, this.composePage(grid, page, measurer), images);
                } catch (error) {
                    this._notificationService.warn("Error processing one page, continuing with others.");
                    continue;
                }
            }

            this.exportStatus = 'Saving PDF…';
            await nextPaint();
            pdf.save(`${this.filename}.pdf`);
        } finally {
            this.finishExport(pages);
        }
        // this._dialogRef.close();
    }

    public async exportAsPowerpoint(): Promise<void> {
        this.isExporting = true;
        // Snapshot: see exportAsPdf.
        const pages: Array<Array<IGalleryItem>> = this.galleryItemsPages;
        const pptx: PptxGenJS = new PptxGenJS();
        let slidesAdded: number = 0;

        try {
            pptx.defineLayout({ name: 'A3', width: 16.5, height: 11.7 });
            pptx.defineLayout({ name: 'A4', width: 11.7, height: 8.3 });
            pptx.layout = this.selectedPageSize.name;

            // pptxgenjs has no text metrics, so wrapping is measured with jsPDF's Helvetica and drawn
            // in Arial, which has the same widths.
            const measurer: ITextMeasurer = createJsPdfMeasurer(new jsPDF({ unit: 'in' }));
            const grid: IPageGrid = this.computeGrid(this.selectedPageSize.width, this.selectedPageSize.height);
            const images: Map<string, IExportImage | null> = await this.loadImages(grid, pages);

            for (const [index, page] of pages.entries()) {
                await this.showBuildProgress(pages, index);
                try {
                    this.drawSlide(pptx, pptx.addSlide(), this.composePage(grid, page, measurer), images);
                    slidesAdded++;
                } catch (error) {
                    this._notificationService.warn("Error processing one slide, continuing with others.");
                    continue;
                }
            }

            // A deck with no slides is itself a file PowerPoint will not open, so report the failure
            // rather than handing over a dud. The dialog stays open so the export can be retried.
            if (slidesAdded === 0) {
                this._notificationService.error('No slides could be rendered, so no PowerPoint was created.');

                return;
            }

            try {
                this.exportStatus = 'Saving PowerPoint…';
                await pptx.writeFile({ fileName: `${this.filename}.pptx` });
            } catch (error) {
                this._notificationService.error("Failed to save PowerPoint file.");
            } finally {
                this._dialogRef.close();
            }
        } finally {
            this.finishExport(pages);
        }
    }

    public close(): void {
        this._dialogRef.close();
    }

    private setInitialLayoutConfig(): void {
        this.selectedConfig = this.layoutConfigs.filter(
            (config: { layout: string; pageSize: number }) => config.layout === '6x3'
        )[0];
    }

    private initializeGallery(): void {
        this.galleryItemsPages = _.chunk(
            this.data.products.slice(0, this.exportCap).map((item: IProduct & { imageId: string }) => ({
                ...item,
                image_url: this._sanitizer.bypassSecurityTrustUrl(item.imageId)
            })),
            this.selectedConfig.pageSize
        );

        const page: Array<IProduct & { image_url: SafeUrl }> = this.galleryItemsPages[this.galleryItemsPages.length -1];
        let remainingImageSlots: number = this.selectedConfig.pageSize - page.length;
        while (remainingImageSlots > 0) {
            page.push(undefined);
            remainingImageSlots--;
        }

        this.galleryItems = this.galleryItemsPages[0];
        this.previewPageIndex = 0;
        this.availableMetrics = [...Object.keys(this.galleryItems[0])];
    }

    private computeGrid(pageWidth: number, pageHeight: number): IPageGrid {
        const { cols, rows }: { cols: number; rows: number } = parseLayout(this.selectedConfig.layout);

        return computePageGrid(pageWidth, pageHeight, cols, rows, this.fileHeader?.length > 0, this.fileSubHeader?.length > 0);
    }

    // Keyed by URL: every "no image" tile shares one placeholder URL, so it is fetched once.
    private async loadImages(grid: IPageGrid, pages: Array<Array<IGalleryItem>>): Promise<Map<string, IExportImage | null>> {
        const items: Array<IGalleryItem> = _.flatten(pages).filter(Boolean);
        // In page order, so the preview can follow the work from the first page to the last.
        const urls: Array<string> = _.uniq(items.map((item: IGalleryItem) => item.imageId));
        const largestTile: number = Math.max(grid.tiles[0].w, grid.tiles[0].h);
        const maxPixels: number = Math.ceil(largestTile * EXPORT_IMAGE_DPI);

        urls.forEach((url: string) => this._imageStatus.set(url, 'pending'));
        this.showImageProgress(pages, items);

        const loaded: Array<IExportImage | null> = await mapWithConcurrency(
            urls,
            IMAGE_LOAD_CONCURRENCY,
            async (url: string) => {
                this._imageStatus.set(url, 'loading');
                this.showImageProgress(pages, items);
                const image: IExportImage | null = await loadExportImage(url, maxPixels);
                this._imageStatus.set(url, image ? 'done' : 'failed');
                this.showImageProgress(pages, items);

                return image;
            }
        );
        const images: Map<string, IExportImage | null> = new Map(
            urls.map((url: string, index: number): [string, IExportImage | null] => [url, loaded[index]])
        );

        const failed: number = items.filter((item: IGalleryItem) => !images.get(item.imageId)).length;
        if (failed > 0) {
            this._notificationService.warn(`${failed} image(s) could not be loaded and are shown as grey boxes.`);
        }

        return images;
    }

    // Keeps the preview on the first page that still has images in flight, so the user watches
    // the export move through the pages rather than staring at a static page 1.
    private showImageProgress(pages: Array<Array<IGalleryItem>>, items: Array<IGalleryItem>): void {
        const isFinished: (item: IGalleryItem) => boolean = (item: IGalleryItem): boolean =>
            ['done', 'failed'].includes(this._imageStatus.get(item.imageId));
        const finished: number = items.filter(isFinished).length;
        const firstUnfinished: number = pages.findIndex((page: Array<IGalleryItem>) =>
            page.some((item: IGalleryItem) => item && !isFinished(item)));
        const pageIndex: number = firstUnfinished === -1 ? pages.length - 1 : firstUnfinished;

        this.showPreviewPage(pages, pageIndex);
        this.exportStatus = `Loading images ${finished} of ${items.length} · page ${pageIndex + 1} of ${pages.length}`;
    }

    private async showBuildProgress(pages: Array<Array<IGalleryItem>>, pageIndex: number): Promise<void> {
        this.showPreviewPage(pages, pageIndex);
        this.exportStatus = `Building page ${pageIndex + 1} of ${pages.length}…`;
        // Drawing a page is synchronous; without a paint first the preview and status would
        // freeze on the previous page until the whole file was built.
        await nextPaint();
    }

    private showPreviewPage(pages: Array<Array<IGalleryItem>>, pageIndex: number): void {
        if (this.galleryItems !== pages[pageIndex]) {
            this.galleryItems = pages[pageIndex];
        }
        this.previewPageIndex = pageIndex;
    }

    private finishExport(pages: Array<Array<IGalleryItem>>): void {
        this.isExporting = false;
        this.exportStatus = '';
        this._imageStatus.clear();
        // Only rewind if the user has not re-paged the gallery (e.g. changed layout) meanwhile.
        if (this.galleryItemsPages === pages) {
            this.showPreviewPage(pages, 0);
        }
    }

    private composePage(grid: IPageGrid, page: Array<IGalleryItem>, measurer: ITextMeasurer): IComposedPage {
        const showMetrics: boolean = this.data.filterData.length > 0;
        const alignTop: boolean = this.data.metricAlignment !== false;

        return {
            header: grid.header && composeHeaderBar(grid.header, this.fileHeader, HEADER_FONT_RATIO, measurer),
            subHeader: grid.subHeader && composeHeaderBar(grid.subHeader, this.fileSubHeader, SUB_HEADER_FONT_RATIO, measurer),
            tiles: grid.tiles.map((box: IBox, index: number) => {
                const item: IGalleryItem = page[index];

                return {
                    box,
                    item,
                    overlay: item && showMetrics ? composeOverlay(box, this.metricRows(item), alignTop, measurer) : undefined
                };
            })
        };
    }

    // Same label and value formatting as the preview overlay in the template.
    private metricRows(item: IGalleryItem): Array<string> {
        return this.data.filterData.map((filter: IDisplayMetric) => {
            const label: string = this._displayName.transform(filter.metric_acronym) ?? '';
            const value: unknown = item[filter.metric_name];
            const shown: unknown = filter.metric_name.endsWith('_v') || filter.metric_name === 'reg_sales'
                ? this._shortNumber.transform(value as number)
                : value ?? '';

            return `${label}: ${shown}`;
        });
    }

    private drawPdfPage(pdf: jsPDF, page: IComposedPage, images: Map<string, IExportImage | null>): void {
        if (page.header) {
            this.drawPdfBar(pdf, page.header, HEADER_COLOUR);
        }
        if (page.subHeader) {
            this.drawPdfBar(pdf, page.subHeader, SUB_HEADER_COLOUR);
        }

        for (const tile of page.tiles) {
            const { x, y, w, h }: IBox = tile.box;
            pdf.setFillColor(TILE_COLOUR);
            pdf.rect(x, y, w, h, 'F');

            if (!tile.item) {
                continue;
            }

            const image: IExportImage | null = images.get(tile.item.imageId);
            if (image) {
                const fitted: IBox = fitContain(tile.box, image.width, image.height);
                // A stable alias lets jsPDF embed an image once even if it is drawn on several pages.
                pdf.addImage(image.dataUrl, 'JPEG', fitted.x, fitted.y, fitted.w, fitted.h, tile.item.imageId, 'NONE');
            } else {
                pdf.setFillColor(MISSING_IMAGE_COLOUR);
                pdf.rect(x, y, w, h, 'F');
            }

            if (tile.overlay?.lines.length > 0) {
                const overlay: ITextBlock = tile.overlay;
                pdf.saveGraphicsState();
                pdf.setGState(new GState({ opacity: OVERLAY_OPACITY }));
                pdf.setFillColor(OVERLAY_COLOUR);
                pdf.rect(overlay.box.x, overlay.box.y, overlay.box.w, overlay.box.h, 'F');
                pdf.restoreGraphicsState();

                this.setPdfFont(pdf, overlay.fontPt);
                pdf.text(overlay.lines, overlay.box.x + overlay.paddingIn, overlay.box.y + overlay.paddingIn, {
                    baseline: 'top',
                    lineHeightFactor: LINE_HEIGHT_FACTOR
                });
            }
        }
    }

    private drawPdfBar(pdf: jsPDF, bar: ITextBlock, colour: string): void {
        pdf.setFillColor(colour);
        pdf.rect(bar.box.x, bar.box.y, bar.box.w, bar.box.h, 'F');
        this.setPdfFont(pdf, bar.fontPt);
        pdf.text(bar.lines[0], bar.box.x + bar.paddingIn, bar.box.y + bar.box.h / 2, { baseline: 'middle' });
    }

    private setPdfFont(pdf: jsPDF, fontPt: number): void {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(fontPt);
        pdf.setTextColor(TEXT_COLOUR);
    }

    private drawSlide(
        pptx: PptxGenJS,
        slide: PptxGenJS.Slide,
        page: IComposedPage,
        images: Map<string, IExportImage | null>
    ): void {
        if (page.header) {
            this.drawSlideText(slide, page.header, { color: hex(HEADER_COLOUR) }, 'middle');
        }
        if (page.subHeader) {
            this.drawSlideText(slide, page.subHeader, { color: hex(SUB_HEADER_COLOUR) }, 'middle');
        }

        for (const tile of page.tiles) {
            slide.addShape(pptx.ShapeType.rect, { ...tile.box, fill: { color: hex(TILE_COLOUR) } });

            if (!tile.item) {
                continue;
            }

            const image: IExportImage | null = images.get(tile.item.imageId);
            if (image) {
                // `data`, not `path`: pptxgenjs derives the media file extension by string-
                // slicing `path`, and the base64 alphabet contains '/', so a data URI there
                // yields a garbage extension ("...suvork5cyii=") which it writes into
                // [Content_Types].xml and the media Target. PowerPoint then rejects the whole
                // package. Only the `data` branch parses the real mime type.
                slide.addImage({ data: image.dataUrl, ...fitContain(tile.box, image.width, image.height) });
            } else {
                slide.addShape(pptx.ShapeType.rect, { ...tile.box, fill: { color: hex(MISSING_IMAGE_COLOUR) } });
            }

            if (tile.overlay?.lines.length > 0) {
                this.drawSlideText(slide, tile.overlay, { color: hex(OVERLAY_COLOUR), transparency: (1 - OVERLAY_OPACITY) * 100 }, 'top');
            }
        }
    }

    private drawSlideText(slide: PptxGenJS.Slide, block: ITextBlock, fill: PptxGenJS.ShapeFillProps, valign: PptxGenJS.VAlign): void {
        // Lines are pre-wrapped by the measurer so the box height matches; letting PowerPoint
        // re-wrap as well could push the last line outside the overlay.
        slide.addText(
            block.lines.map((line: string, index: number) => ({
                text: line,
                options: { breakLine: index < block.lines.length - 1 }
            })),
            {
                ...block.box,
                fill,
                fontFace: 'Arial',
                fontSize: block.fontPt,
                bold: true,
                color: hex(TEXT_COLOUR),
                valign,
                wrap: false,
                fit: 'none',
                margin: block.paddingIn * 72,
                lineSpacing: block.fontPt * LINE_HEIGHT_FACTOR,
                paraSpaceBefore: 0,
                paraSpaceAfter: 0
            }
        );
    }
}
